import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ButtonRow,
  Dropdown,
  DropdownItem,
  Root,
  StyledButton,
  Caret,
} from "./actionDropdownButton.styled";

export type DropdownOption = {
  id: string;
  label: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
};

interface ActionDropdownButtonProps {
  children: React.ReactNode;
  action?: boolean;
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  className?: string;
  style?: React.CSSProperties;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
  alignRight?: boolean;
  width?: number;
  options: DropdownOption[];
  dropdownWidth?: number;
}

export const ActionDropdownButton: React.FC<ActionDropdownButtonProps> = (
  props
) => {
  const { children, options, disabled } = props;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const [open, setOpen] = useState(false);

  const hasEnabledOptions = useMemo(
    () => options.some((o) => !o.disabled),
    [options]
  );

  const canOpen = !disabled && hasEnabledOptions;

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const toggleOpen = () => {
    if (!canOpen) return;
    setOpen((v) => !v);
  };

  const handleItemClick = (opt: DropdownOption) => {
    if (opt.disabled) return;
    opt.onClick();
    setOpen(false);
    buttonRef.current?.focus();
  };

  return (
    <Root
      ref={rootRef}
      className={props.className}
      style={props.style}
      alignRight={props.alignRight}
      marginTop={props.marginTop}
      marginRight={props.marginRight}
      marginBottom={props.marginBottom}
      marginLeft={props.marginLeft}
      width={props.width}
      fullWidth={props.fullWidth}
    >
      <StyledButton
        ref={buttonRef}
        action={props.action}
        size={props.size}
        fullWidth={props.fullWidth}
        disabled={props.disabled}
        type={props.type ?? "button"}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggleOpen}
        onKeyDown={(e) => {
          if (!canOpen) return;

          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleOpen();
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <ButtonRow>
          <span>{children}</span>
          <Caret aria-hidden>▾</Caret>
        </ButtonRow>
      </StyledButton>

      {open && (
        <Dropdown role="menu" width={props.dropdownWidth}>
          {options.map((opt) => (
            <DropdownItem
              key={opt.id}
              role="menuitem"
              tabIndex={opt.disabled ? -1 : 0}
              disabled={opt.disabled}
              onClick={() => handleItemClick(opt)}
              onKeyDown={(e) => {
                if (opt.disabled) return;

                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleItemClick(opt);
                }
              }}
            >
              {opt.label}
            </DropdownItem>
          ))}
        </Dropdown>
      )}
    </Root>
  );
};
